import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAuthToken } from '@/lib/authToken';

// Back handler stack: only the top (deepest) handler runs when Android back is pressed.
// This prevents multiple handlers (Dashboard, SupplierPage, SupplierInvoicesPayments) from all firing.
const backHandlerStack: Array<() => string | void> = [];
let capacitorListenerHandle: { remove: () => Promise<void> } | null = null;
let iosTouchListenerAdded = false;
let mobileWebHandlerAdded = false;

function pushBackHandler(handler: () => string | void) {
  backHandlerStack.push(handler);
}

function popBackHandler(handler: () => string | void) {
  const i = backHandlerStack.lastIndexOf(handler);
  if (i >= 0) backHandlerStack.splice(i, 1);
}

function runTopBackHandler(): string | void {
  const top = backHandlerStack[backHandlerStack.length - 1];
  if (top) return top();
  return undefined;
}

// Type declarations for global objects
declare global {
  interface Window {
    Capacitor?: any;
    AndroidBackButton?: {
      exitApp: () => void;
    };
    webkit?: {
      messageHandlers?: {
        exitApp?: {
          postMessage: (message: any) => void;
        };
      };
    };
    handleBackNavigation?: () => string;
  }
}

interface UseBackNavigationOptions {
  onBack?: () => void;
  canGoBack?: boolean;
  showExitConfirmation?: boolean;
  /** Called when user confirms exit on mobile web - use to show login (logout). Only used when !isNative && (Android|iOS) */
  onMobileWebExitConfirm?: () => void;
}

export function useBackNavigation(options: UseBackNavigationOptions = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { onBack, canGoBack = true, showExitConfirmation = true, onMobileWebExitConfirm } = options;

  // Detect platform
  const isNative = !!((window as any).Capacitor && typeof (window as any).Capacitor.isNativePlatform === 'function' && (window as any).Capacitor.isNativePlatform());
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isMobile = isAndroid || isIOS;
  const isAndroidWeb = isAndroid && !isNative;
  const isMobileWeb = !isNative && (isAndroid || isIOS); // Mobile browser (not native app)

  // Check if we're on the main dashboard
  const isOnDashboard = location.pathname === '/' || location.pathname === '/dashboard';

  // Double-back to exit (Android only): first back does nothing, second within 2s shows exit
  const lastBackPressRef = useRef(0);

  // Handle back navigation
  const handleBack = useCallback(() => {
    console.log('🔄 Back navigation triggered', {
      pathname: location.pathname,
      isOnDashboard,
      canGoBack,
      historyLength: window.history.length,
      hasCustomHandler: !!onBack,
      isAndroid,
      onBackType: typeof onBack,
      onBackFunction: onBack?.toString?.()?.substring(0, 100)
    });

    // If custom back handler is provided, use it (this handles subpages like InventorySection)
    if (onBack) {
      console.log('✅ Using custom back handler');
      try {
        onBack();
        console.log('✅ Custom back handler executed successfully');
        return 'handled';
      } catch (error) {
        console.error('❌ Error executing custom back handler:', error);
        return 'error';
      }
    }

    // Check if we can navigate back in React Router
    const canNavigateBack = canGoBack && window.history.length > 1;
    
    // On dashboard: Android (native+web) and mobile web use double-back-to-exit; others show exit immediately
    if (isOnDashboard) {
      if (showExitConfirmation) {
        const useDoubleBack = isAndroid || isMobileWeb; // Same flow as Android on mobile site
        if (useDoubleBack) {
          const now = Date.now();
          if (now - lastBackPressRef.current < 2000) {
            lastBackPressRef.current = 0;
            showExitDialog();
            return 'exit';
          }
          lastBackPressRef.current = now;
          return 'handled';
        }
        showExitDialog();
      }
      return 'exit';
    }

    // If we're not on the dashboard, try to navigate back
    if (!isOnDashboard && canNavigateBack) {
      console.log('✅ Not on dashboard, navigating back');
      navigate(-1);
      return 'handled';
    }

    // If we can't go back and we're not on dashboard, show exit confirmation
    if (!canNavigateBack) {
      console.log('✅ Cannot go back and not on dashboard - showing exit confirmation');
      if (showExitConfirmation) {
        showExitDialog();
      }
      return 'exit';
    }

    return 'handled';
  }, [onBack, canGoBack, showExitConfirmation, navigate, location.pathname, isOnDashboard, isAndroid, isMobileWeb]);

  // Show exit confirmation dialog
  const showExitDialog = useCallback(() => {
    // Check if dialog already exists
    if (document.getElementById('exit-confirmation-dialog')) {
      return;
    }

    const dialog = document.createElement('div');
    dialog.id = 'exit-confirmation-dialog';
    dialog.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 16px;
      padding: 24px;
      margin: 20px;
      max-width: 320px;
      width: calc(100% - 40px);
      text-align: center;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      animation: slideIn 0.3s ease-out;
      -webkit-tap-highlight-color: transparent;
    `;

    // Mobile web: "close the site" + OK/Cancel. Native (Android/iOS): "exit the app" + Yes/No
    const isMobileWebDialog = isMobileWeb;
    const title = document.createElement('h3');
    title.textContent = isMobileWebDialog ? 'Exit Site?' : 'Exit App?';
    title.style.cssText = `
      margin: 0 0 12px 0;
      font-size: 20px;
      font-weight: 600;
      color: #1f2937;
    `;

    const message = document.createElement('p');
    message.textContent = isMobileWebDialog ? 'Are you sure you want to close the site?' : 'Are you sure you want to exit the app?';
    message.style.cssText = `
      margin: 0 0 24px 0;
      color: #6b7280;
      font-size: 16px;
      line-height: 1.5;
    `;

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: center;
    `;

    const noButton = document.createElement('button');
    noButton.textContent = isMobileWebDialog ? 'Cancel' : 'No';
    noButton.style.cssText = `
      padding: 12px 24px;
      border: 1px solid #d1d5db;
      background: white;
      border-radius: 8px;
      color: #374151;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      flex: 1;
      transition: all 0.2s;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    `;

    const yesButton = document.createElement('button');
    yesButton.textContent = isMobileWebDialog ? 'OK' : 'Yes';
    yesButton.style.cssText = `
      padding: 12px 24px;
      border: none;
      background: #ef4444;
      border-radius: 8px;
      color: white;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      flex: 1;
      transition: all 0.2s;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    `;

    // Add hover effects (only for non-touch devices)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) {
      noButton.addEventListener('mouseenter', () => {
        noButton.style.background = '#f9fafb';
      });
      noButton.addEventListener('mouseleave', () => {
        noButton.style.background = 'white';
      });

      yesButton.addEventListener('mouseenter', () => {
        yesButton.style.background = '#dc2626';
      });
      yesButton.addEventListener('mouseleave', () => {
        yesButton.style.background = '#ef4444';
      });
    }

    // Add touch feedback for mobile devices
    if (isTouchDevice) {
      noButton.addEventListener('touchstart', () => {
        noButton.style.background = '#f9fafb';
      });
      noButton.addEventListener('touchend', () => {
        noButton.style.background = 'white';
      });

      yesButton.addEventListener('touchstart', () => {
        yesButton.style.background = '#dc2626';
      });
      yesButton.addEventListener('touchend', () => {
        yesButton.style.background = '#ef4444';
      });
    }

    noButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (document.getElementById('exit-confirmation-dialog')) {
          document.body.removeChild(dialog);
        }
      } catch {}
    });

    yesButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Remove dialog immediately to avoid any lingering overlays
      try {
        if (document.getElementById('exit-confirmation-dialog')) {
          document.body.removeChild(dialog);
        }
      } catch {}

      // Mobile web: OK -> show login (logout)
      if (isMobileWeb) {
        console.log('🔄 Mobile Web: User confirmed exit, showing login');
        if (onMobileWebExitConfirm) {
          onMobileWebExitConfirm();
        } else {
          // Fallback: clear auth and dispatch event for Index to show login
          clearAuthToken();
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('username');
          window.dispatchEvent(new CustomEvent('mobile-web-logout'));
        }
        return;
      }

      // Native Android/iOS app: exit app
      // Prefer Capacitor App.exitApp on Android
      try {
        if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App && typeof window.Capacitor.Plugins.App.exitApp === 'function') {
          window.Capacitor.Plugins.App.exitApp();
          return;
        }
      } catch {}

      // Alternative Capacitor namespace (some builds expose App directly)
      try {
        if ((window as any).App && typeof (window as any).App.exitApp === 'function') {
          (window as any).App.exitApp();
          return;
        }
      } catch {}

      // Legacy Android interface fallback
      if (window.AndroidBackButton && window.AndroidBackButton.exitApp) {
        window.AndroidBackButton.exitApp();
        return;
      }
      
      // iOS WKWebView message bridge
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.exitApp) {
        window.webkit.messageHandlers.exitApp.postMessage({});
        return;
      }

      // Browser fallback
      window.close();
    });

    buttonContainer.appendChild(noButton);
    buttonContainer.appendChild(yesButton);
    content.appendChild(title);
    content.appendChild(message);
    content.appendChild(buttonContainer);
    dialog.appendChild(content);

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: scale(0.9) translateY(-20px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(dialog);

    // Auto-close dialog if user taps outside
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        document.body.removeChild(dialog);
      }
    });

    // Focus management (skip on touch devices to avoid keyboard popup)
    if (!('ontouchstart' in window)) {
      noButton.focus();
    }
  }, [isAndroidWeb, isMobileWeb, onMobileWebExitConfirm, navigate]);

  // Desktop: Intercept browser back when we have custom onBack (e.g. report pages -> go to Reports tab)
  useEffect(() => {
    if (isMobile || !onBack || isOnDashboard) return;

    const handlePopState = () => {
      onBack();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile, onBack, isOnDashboard]);

  // Register this handler in the stack so only the deepest (top) handler runs
  const handlerRef = useRef(handleBack);
  handlerRef.current = handleBack;

  useEffect(() => {
    if (!isMobile) return;

    const handler = () => handlerRef.current();
    pushBackHandler(handler);

    return () => {
      popBackHandler(handler);
    };
  }, [isMobile, handleBack]); // handleBack in deps so we update handlerRef when it changes

  // Single global Capacitor listener - add once, never remove. Calls top handler from stack.
  useEffect(() => {
    if (!isMobile || !isAndroid) return;

    window.handleBackNavigation = () => {
      console.log('🌐 Global back navigation handler called');
      return runTopBackHandler();
    };

    if (capacitorListenerHandle) return; // Already added

    (async () => {
      try {
        let handle: { remove: () => Promise<void> } | null = null;
        if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App) {
          handle = await window.Capacitor.Plugins.App.addListener('backButton', () => {
            console.log('Android back button pressed');
            runTopBackHandler();
          });
        } else if (window.Capacitor) {
          handle = await window.Capacitor.addListener('backButton', () => {
            console.log('Android back button pressed (fallback)');
            runTopBackHandler();
          });
        }
        if (handle) capacitorListenerHandle = handle;
      } catch (error) {
        console.log('Failed to add Capacitor back button listener:', error);
      }
    })();
  }, [isMobile, isAndroid]);

  // iOS-specific setup - single global touch listener, add once (like Capacitor)
  useEffect(() => {
    if (!isMobile || !isIOS) return;
    if (iosTouchListenerAdded) return;

    let startX = 0;
    let startY = 0;
    let isSwiping = false;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      isSwiping = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping) {
        const touch = e.touches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 50 && startX < 50) {
          isSwiping = true;
        }
      }
    };

    // Use changedTouches to compute deltaX in touchend
    const handleTouchEndFixed = (e: TouchEvent) => {
      if (isSwiping && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        const deltaX = touch.clientX - startX;
        if (deltaX > 100) {
          runTopBackHandler();
        }
      }
      isSwiping = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEndFixed, { passive: true });
    iosTouchListenerAdded = true;
  }, [isMobile, isIOS]);

  // Mobile Web (Android + iOS): Single handler, same flow as Android - add once, never remove
  useEffect(() => {
    if (!isMobileWeb) return;
    if (mobileWebHandlerAdded) return;

    let hasGuardState = false;
    const pushGuardState = () => {
      try {
        if (!hasGuardState) {
          window.history.pushState({ mobileWebBackGuard: true }, '');
          hasGuardState = true;
        }
      } catch (error) {
        console.error('Failed to push mobile web guard state:', error);
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      // User pressed back: event.state is the entry we navigated TO (previous). Our guard was on the
      // entry we left - so we must always run our handler on popstate (user intended to go back).
      const result = runTopBackHandler();
      if (result === 'handled' || result === 'exit') {
        // Re-push guard to stay in app for next back press
        setTimeout(() => pushGuardState(), 100);
        return;
      }
      setTimeout(() => pushGuardState(), 100);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.keyCode === 8 && !['INPUT', 'TEXTAREA'].includes((event.target as HTMLElement)?.tagName)) {
        event.preventDefault();
        event.stopPropagation();
        runTopBackHandler();
        return false;
      }
    };

    pushGuardState();
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('keydown', handleKeyDown, true);
    mobileWebHandlerAdded = true;
  }, [isMobileWeb]);

  return {
    handleBack,
    isAndroid,
    isIOS,
    isMobile,
    canGoBack: canGoBack && window.history.length > 1,
    isOnDashboard
  };
}
